import { FC, useEffect, useState } from "react";
import { useFormContext, useFormState, useWatch } from "react-hook-form";
import { AlertTriangle } from "lucide-react";
import { useAzureFileUpload } from "src/components/AssetUploaders/useAzureFileUpload";
import ImageUpload from "./ImageUpload";
import get from "lodash.get";

interface Props {
	stepNumber: number;
	order: number;
	imageProfile: ImageProfile | undefined;
}

const ImageUploadStep: FC<Props> = ({ stepNumber, order, imageProfile }) => {
	const { control, setValue } = useFormContext<BannerFormState>();

	const [uuid, setUUID] = useState<string | null>(null);

	const { progress, done, error, uploading, upload } = useAzureFileUpload(
		(uuidList) => {
			if (!uuidList) return;

			if (Array.isArray(uuidList)) {
				setUUID(uuidList[0]);

				setValue(
					`steps.${stepNumber - 1}.fields.${order - 1}.file`,
					uuidList[0]
				);
			}
		}
	);

	const placeholder = useWatch({
		name: `steps.${stepNumber - 1}.fields.${order - 1}.placeholder`,
		control
	});

	const { errors } = useFormState({ control });

	const isAnswerInvalid =
		Boolean(
			get(errors, `steps[${stepNumber - 1}].fields[${order - 1}]`, null)
		) || error;

	useEffect(() => {
		setValue(`steps.${stepNumber - 1}.fields.${order - 1}.error`, error);
	}, [error]);

	useEffect(() => {
		setValue(
			`steps.${stepNumber - 1}.fields.${order - 1}.uploading`,
			uploading
		);
	}, [uploading]);

	return (
		<div>
			{error && (
				<div className="flex items-center gap-1.5 text-destructive mb-3">
					<AlertTriangle size={16} />
					<span className="text-sm">
						Problem uploading the image. Please try again.
					</span>
				</div>
			)}

			<div
				className={
					isAnswerInvalid || error
						? "rounded-lg border border-destructive p-2"
						: ""
				}
			>
				<ImageUpload
					stepNumber={stepNumber}
					order={order}
					placeholder={placeholder as string}
					control={control}
					error={error}
					upload={(file: File, uploadType: string) =>
						upload(file, uploadType, undefined, imageProfile)
					}
					uuid={uuid}
					done={done}
					uploading={uploading}
					setValue={setValue}
					progress={progress}
					imageProfile={imageProfile}
				/>
			</div>
		</div>
	);
};

export default ImageUploadStep;
